import { useEffect } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { getShortUrls } from '../api/getShortUrls';
import { useShortUrlsState } from '../context';
import type { ShortUrlEntry, ShortUrlInfo, ShortUrlsCacheState } from '../context/types';

function collectEntries(queue: readonly string[], cache: ShortUrlsCacheState['cache']): ShortUrlEntry[] {
	return queue.reduce<ShortUrlEntry[]>((acc, longUrl) => {
		const cacheState = cache[longUrl];
		if (!cacheState || cacheState.status !== 'loading') return acc;
		acc.push({
			longUrl,
			normalizedLongUrl: cacheState.normalizedLongUrl,
			localShareId: cacheState.localShareId,
		});
		return acc;
	}, []);
}

// Falls back to the local hash if the shortener response doesn't parse as a URL.
function shareIdFromShortUrl(shortUrl: string): string {
	try {
		return new URL(shortUrl).pathname.split('/').filter(Boolean).at(-1) ?? '';
	} catch {
		return shortUrl.split('/').filter(Boolean).at(-1) ?? '';
	}
}

function buildLoadedInfos(
	entries: readonly ShortUrlEntry[],
	shortUrls: Readonly<Record<string, string>>,
): Record<string, ShortUrlInfo> {
	return entries.reduce<Record<string, ShortUrlInfo>>((acc, entry) => {
		const shortUrl = shortUrls[entry.longUrl];
		if (!shortUrl) return acc;

		// oxlint-disable-next-line no-param-reassign
		acc[entry.longUrl] = {
			url: shortUrl,
			trackingProperties: {
				generated_share_id: shareIdFromShortUrl(shortUrl) || entry.localShareId,
				generated_share_id_source: 'shortener',
			},
		};
		return acc;
	}, {});
}

export function useLoadQueue(): void {
	const [state, { removeFromQueue, setLoaded, setError }] = useShortUrlsState();
	const utilityContext = useUtilityContext();

	useEffect(() => {
		if (!state.queue.length) return;
		// copying the contents because queue is mutated by the state mutators
		const queue = [...state.queue];
		const entries = collectEntries(queue, state.cache);
		(async () => {
			// removing the items from the queue to make sure we're not making the request multiple times
			removeFromQueue(queue);
			if (!entries.length) return;
			try {
				const shortUrls = await getShortUrls(entries, utilityContext);
				setLoaded(buildLoadedInfos(entries, shortUrls));
			} catch (e) {
				setError(entries);
			}
		})();
	}, [utilityContext, state, removeFromQueue, setLoaded, setError]);
}
